import React, { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import Swal from 'sweetalert2';
import { X, Upload, FileSpreadsheet, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ImportModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setImportResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const importMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await axios.post(`${API_URL}/api/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (data) => {
      setImportResult(data);
      onSuccess?.();
      
      if (data.failed === 0 && data.skipped === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Import Complete',
          html: `<strong>${data.success}</strong> cards imported successfully.`,
          confirmButtonColor: '#D0021B',
          customClass: { title: 'font-serif' },
        });
      } else {
        Swal.fire({
          icon: data.failed > 0 ? 'warning' : 'success',
          title: 'Import Complete',
          html: `
            <strong>${data.success}</strong> cards imported successfully.<br/>
            ${data.skipped > 0 ? `<strong>${data.skipped}</strong> duplicates skipped.<br/>` : ''}
            ${data.failed > 0 ? `<strong>${data.failed}</strong> rows failed.` : ''}
          `,
          confirmButtonColor: '#D0021B',
          customClass: { title: 'font-serif' },
        });
      }
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: 'Import Failed',
        text: error.response?.data?.detail || 'Failed to import file',
        confirmButtonColor: '#D0021B',
        customClass: { title: 'font-serif' },
      });
    },
  });

  const handleImport = () => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    importMutation.mutate(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
        className="bg-white rounded-lg max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="import-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
          <h2 className="font-serif text-xl text-[#1A1A1A]">Import Press Cards</h2>
          <button
            onClick={onClose}
            className="p-2 text-[#666666] hover:text-[#1A1A1A] hover:bg-[#F5F5F5] rounded-lg transition-colors"
            data-testid="close-import-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-[#D0021B] bg-[#D0021B]/5'
                : 'border-[#E5E5E5] hover:border-[#D0021B]/50'
            }`}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-[#D0021B]' : 'text-[#666666]'}`} />
            {isDragActive ? (
              <p className="text-[#D0021B] font-medium">Drop the file here...</p>
            ) : (
              <>
                <p className="text-[#1A1A1A] font-medium mb-1">Drag & drop Excel file here</p>
                <p className="text-[#666666] text-sm">or click to browse</p>
              </>
            )}
            <p className="text-xs text-[#666666] mt-4">Supported formats: .xlsx, .xls</p>
          </div>

          {/* Selected File */}
          {file && (
            <div className="mt-4 p-4 bg-[#F5F5F5] rounded-lg flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-[#16A34A]" />
              <div className="flex-1">
                <p className="font-medium text-[#1A1A1A]">{file.name}</p>
                <p className="text-xs text-[#666666]">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  setImportResult(null);
                }}
                className="p-1 text-[#666666] hover:text-[#DC2626]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="mt-4 p-4 border border-[#E5E5E5] rounded-lg">
              <h3 className="font-medium text-[#1A1A1A] mb-3">Import Results</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <CheckCircle className="w-6 h-6 text-[#16A34A] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#16A34A]">{importResult.success}</p>
                  <p className="text-xs text-[#666666]">Imported</p>
                </div>
                <div className="text-center">
                  <AlertTriangle className="w-6 h-6 text-[#D97706] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#D97706]">{importResult.skipped}</p>
                  <p className="text-xs text-[#666666]">Skipped</p>
                </div>
                <div className="text-center">
                  <XCircle className="w-6 h-6 text-[#DC2626] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#DC2626]">{importResult.failed}</p>
                  <p className="text-xs text-[#666666]">Failed</p>
                </div>
              </div>

              {importResult.errors?.length > 0 && (
                <div className="mt-4 max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-[#DC2626] mb-2">Errors:</p>
                  {importResult.errors.map((err, idx) => (
                    <p key={idx} className="text-xs text-[#666666]">
                      Row {err.row}: {err.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Expected Format */}
          <div className="mt-4 p-4 bg-[#F5F5F5] rounded-lg">
            <p className="text-xs font-bold text-[#1A1A1A] mb-2">Expected Excel Columns:</p>
            <p className="text-xs text-[#666666] font-mono">
              Id | Name | Address | DOE | Designation | Mobile | URL
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E5E5E5]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[#666666] hover:text-[#1A1A1A] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file || importMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-[#D0021B] text-white rounded-lg hover:bg-[#A50015] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shimmer-btn"
            data-testid="import-submit-button"
          >
            {importMutation.isPending ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
